import { parseCSV } from 'map-editor-features/csv/lib/csvParser';
import {
  RECOVERY_CSV_HEADER,
  RecoverySelection,
  recoveryCsv,
} from './recoveryCsv';

function readText(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () =>
      reject(reader.error || new Error('CSV 파일을 읽지 못했습니다.'));
    reader.readAsText(file);
  });
}

// Match complete boundaries regardless of starting vertex or winding; preserve
// the original layer/type when an ordinary extraction already contains a stall.
function boundaryKey(vertices: [number, number][]): string {
  const points = vertices.map(
    ([x, y]) => `${Math.round(x * 1000)},${Math.round(y * 1000)}`,
  );
  if (points.length > 1 && points[0] === points[points.length - 1])
    points.pop();
  return points.sort().join(';');
}

/** Append recovered source geometry in the converted CSV's coordinate frame. */
export async function mergeRecoveryCsv(
  converted: File,
  recovery: RecoverySelection,
  offset?: { x: number; y: number },
): Promise<File> {
  if (!offset || !Number.isFinite(offset.x) || !Number.isFinite(offset.y)) {
    throw new Error(
      '도면의 좌표 기준을 확인하지 못했습니다. 서버를 업데이트한 뒤 다시 변환해주세요.',
    );
  }
  const content = (await readText(converted)).replace(/^\uFEFF/, '');
  const headerEnd = content.indexOf('\n');
  if (
    headerEnd < 0 ||
    content.slice(0, headerEnd).trim() !== RECOVERY_CSV_HEADER
  ) {
    throw new Error(
      '변환된 도면의 CSV 형식이 달라 복원 주차면을 합칠 수 없습니다.',
    );
  }
  const existing = new Set(
    parseCSV(content)
      .entities.filter(
        (entity) =>
          (entity.layer.startsWith('p-parking') || entity.layer === 's-pillar') &&
          entity.isClosed,
      )
      .map((entity) => boundaryKey(entity.points.map(({ x, y }) => [x, y]))),
  );
  const candidates = recovery.candidates.filter((candidate) => {
    const key = boundaryKey(
      candidate.vertices.map(([x, y]) => [x - offset.x, y - offset.y]),
    );
    if (existing.has(key)) return false;
    existing.add(key);
    return true;
  });
  if (!candidates.length) return converted;
  const addition = await readText(
    recoveryCsv(candidates, recovery.sourceHash, [offset.x, offset.y]),
  );
  // Keep the server filename so CCTV/building sidecars still resolve for this import.
  return new File(
    [content.trimEnd(), '\n', addition.slice(addition.indexOf('\n') + 1)],
    converted.name,
    {
      type: 'text/csv',
    },
  );
}
